import { useState } from 'react';
import {
  createResourceGroup,
  validateDeployment,
  deploy,
  createSimulcastResourceGroup,
  validateSimulcastDeployment,
  deploySimulcast,
} from '../../services/tenantProvisioningApi';

const TENANT_NAME_REGEX = /^[a-z0-9\-]+$/;
const MIN_LENGTH = 3;
const MAX_LENGTH = 20;

function TenantProvisioning() {
  const [tenantName, setTenantName] = useState('');
  const [tenantNameTouched, setTenantNameTouched] = useState(false);

  const [step1Done, setStep1Done] = useState(false);
  const [rgLoading, setRgLoading] = useState(false);
  const [rgResult, setRgResult] = useState(null);

  const [validateLoading, setValidateLoading] = useState(false);
  const [validateResult, setValidateResult] = useState(null);

  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  const [deployLoading, setDeployLoading] = useState(false);
  const [deployResult, setDeployResult] = useState(null);

  const [simRgLoading, setSimRgLoading] = useState(false);
  const [simRgResult, setSimRgResult] = useState(null);
  const [simValidateLoading, setSimValidateLoading] = useState(false);
  const [simValidateResult, setSimValidateResult] = useState(null);
  const [simDeployConfirmOpen, setSimDeployConfirmOpen] = useState(false);
  const [simDeployLoading, setSimDeployLoading] = useState(false);
  const [simDeployResult, setSimDeployResult] = useState(null);

  const normalizedName = tenantName.trim().toLowerCase();
  const resourceGroupName = normalizedName ? `${normalizedName}-rsg-prod` : '';
  const simulcastResourceGroupName = normalizedName ? `${normalizedName}-simulcast` : '';
  const tenantNameValid =
    normalizedName.length >= MIN_LENGTH &&
    normalizedName.length <= MAX_LENGTH &&
    TENANT_NAME_REGEX.test(normalizedName);
  const tenantNameError = tenantNameTouched && normalizedName && !tenantNameValid;

  const handleTenantNameChange = (e) => {
    const v = e.target.value;
    setTenantName(v.toLowerCase());
    setTenantNameTouched(true);
    if (rgResult || validateResult || deployResult || simRgResult || simValidateResult || simDeployResult) {
      setRgResult(null);
      setValidateResult(null);
      setDeployResult(null);
      setSimRgResult(null);
      setSimValidateResult(null);
      setSimDeployResult(null);
      setStep1Done(false);
    }
  };

  const handleCreateResourceGroup = async () => {
    if (!tenantNameValid) return;
    setRgResult(null);
    setRgLoading(true);
    try {
      const result = await createResourceGroup(normalizedName);
      setRgResult(result);
      setStep1Done(result.success);
    } catch (err) {
      setRgResult({
        success: false,
        message: err.message || 'Request failed',
        details: err.details?.details ?? err.details?.message,
      });
      setStep1Done(false);
    } finally {
      setRgLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!tenantNameValid || !step1Done) return;
    setValidateResult(null);
    setValidateLoading(true);
    try {
      const result = await validateDeployment(normalizedName);
      setValidateResult(result);
    } catch (err) {
      setValidateResult({
        success: false,
        message: err.message || 'Validation request failed',
        details: err.details?.details ?? err.details?.message,
      });
    } finally {
      setValidateLoading(false);
    }
  };

  const handleDeployClick = () => {
    if (validateResult?.success) setDeployConfirmOpen(true);
  };

  const handleDeployConfirm = async () => {
    setDeployConfirmOpen(false);
    setDeployResult(null);
    setDeployLoading(true);
    try {
      const result = await deploy(normalizedName);
      setDeployResult(result);
    } catch (err) {
      setDeployResult({
        success: false,
        message: err.message || 'Deployment failed',
        details: err.details?.details ?? err.details?.message,
      });
    } finally {
      setDeployLoading(false);
    }
  };

  const handleCreateSimulcastResourceGroup = async () => {
    if (!tenantNameValid || !deployResult?.success) return;
    setSimRgResult(null);
    setSimRgLoading(true);
    try {
      const result = await createSimulcastResourceGroup(normalizedName);
      setSimRgResult(result);
    } catch (err) {
      setSimRgResult({
        success: false,
        message: err.message || 'Request failed',
        details: err.details?.details ?? err.details?.message,
      });
    } finally {
      setSimRgLoading(false);
    }
  };

  const handleValidateSimulcast = async () => {
    if (!tenantNameValid || !simRgResult?.success) return;
    setSimValidateResult(null);
    setSimValidateLoading(true);
    try {
      const result = await validateSimulcastDeployment(normalizedName);
      setSimValidateResult(result);
    } catch (err) {
      setSimValidateResult({
        success: false,
        message: err.message || 'Validation request failed',
        details: err.details?.details ?? err.details?.message,
      });
    } finally {
      setSimValidateLoading(false);
    }
  };

  const handleSimDeployClick = () => {
    if (simValidateResult?.success) setSimDeployConfirmOpen(true);
  };

  const handleSimDeployConfirm = async () => {
    setSimDeployConfirmOpen(false);
    setSimDeployResult(null);
    setSimDeployLoading(true);
    try {
      const result = await deploySimulcast(normalizedName);
      setSimDeployResult(result);
    } catch (err) {
      setSimDeployResult({
        success: false,
        message: err.message || 'Deployment failed',
        details: err.details?.details ?? err.details?.message,
      });
    } finally {
      setSimDeployLoading(false);
    }
  };

  const ResultBox = ({ result }) => (
    <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 dark:bg-green-950 text-green-800' : 'bg-red-50 dark:bg-red-950 text-red-800'}`}>
      <p>{result.message}</p>
      {result.details && <pre className="mt-2 text-xs overflow-auto">{result.details}</pre>}
    </div>
  );

  const Section = ({ title, description, children }) => (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>
      {children}
    </section>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Tenant Provisioning</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Create a new tenant in InnovA-Prod: backoffice and simulcast resource groups with template deployments (South Central US). Complete Steps 1–4 for backoffice first, then Steps 5–7 for simulcast.
        </p>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 space-y-6">
        <Section
          title="Step 1: Tenant name"
          description="Enter the tenant name (lowercase, 3–20 characters, letters, numbers, and hyphens only). This will be used for the resource group and all template resources."
        >
          <div className="space-y-2">
            <label htmlFor="tenant-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant name</label>
            <input
              id="tenant-name"
              type="text"
              value={tenantName}
              onChange={handleTenantNameChange}
              onBlur={() => setTenantNameTouched(true)}
              placeholder="e.g. mytenant"
              autoComplete="off"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100 ${tenantNameError ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'}`}
            />
            {tenantNameError && <span className="text-sm text-red-600">Tenant name must be 3–20 characters, lowercase letters, numbers, and hyphens only.</span>}
            {resourceGroupName && !tenantNameError && (
              <p className="text-sm text-gray-500 dark:text-slate-400">Backoffice resource group: <strong>{resourceGroupName}</strong>. Simulcast resource group: <strong>{simulcastResourceGroupName}</strong>.</p>
            )}
          </div>
        </Section>

        <Section
          title="Step 2: Create resource group"
          description={<>Create the resource group <strong>{resourceGroupName || '{tenantname}-rsg-prod'}</strong> in subscription InnovA-Prod (South Central US).</>}
        >
          <button
            type="button"
            onClick={handleCreateResourceGroup}
            disabled={!tenantNameValid || rgLoading}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 font-medium disabled:opacity-60"
          >
            {rgLoading ? 'Creating…' : 'Create resource group'}
          </button>
          {rgResult && <ResultBox result={rgResult} />}
        </Section>

        <Section
          title="Step 3: Validate deployment"
          description="Validate the backoffice Bicep template (with tenant name replaced) before deploying. Run this after the resource group exists."
        >
          <button
            type="button"
            onClick={handleValidate}
            disabled={!step1Done || validateLoading}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 font-medium disabled:opacity-60"
          >
            {validateLoading ? 'Validating…' : 'Validate template'}
          </button>
          {validateResult && <ResultBox result={validateResult} />}
        </Section>

        <Section
          title="Step 4: Deploy to production"
          description="After validation succeeds, deploy the template to create all backoffice resources. This will create resources in the resource group."
        >
          <button
            type="button"
            onClick={handleDeployClick}
            disabled={!validateResult?.success || deployLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-60"
          >
            {deployLoading ? 'Deploying…' : 'Deploy template'}
          </button>
          {deployResult && <ResultBox result={deployResult} />}
        </Section>

        <hr className="border-gray-200 dark:border-slate-700" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Simulcast deployment (after backoffice)</h2>

        <Section
          title="Step 5: Create simulcast resource group"
          description={<>Create the resource group <strong>{simulcastResourceGroupName || '{tenantname}-simulcast'}</strong> for the simulcast template. Run this after the backoffice deployment (Step 4) succeeds.</>}
        >
          <button
            type="button"
            onClick={handleCreateSimulcastResourceGroup}
            disabled={!deployResult?.success || simRgLoading}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 font-medium disabled:opacity-60"
          >
            {simRgLoading ? 'Creating…' : 'Create simulcast resource group'}
          </button>
          {simRgResult && <ResultBox result={simRgResult} />}
        </Section>

        <Section
          title="Step 6: Validate simulcast deployment"
          description="Validate the simulcast Bicep template (with tenant name replaced) before deploying."
        >
          <button
            type="button"
            onClick={handleValidateSimulcast}
            disabled={!simRgResult?.success || simValidateLoading}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 font-medium disabled:opacity-60"
          >
            {simValidateLoading ? 'Validating…' : 'Validate simulcast template'}
          </button>
          {simValidateResult && <ResultBox result={simValidateResult} />}
        </Section>

        <Section
          title="Step 7: Deploy simulcast to production"
          description="After simulcast validation succeeds, deploy the template to create all simulcast resources."
        >
          <button
            type="button"
            onClick={handleSimDeployClick}
            disabled={!simValidateResult?.success || simDeployLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-60"
          >
            {simDeployLoading ? 'Deploying…' : 'Deploy simulcast template'}
          </button>
          {simDeployResult && <ResultBox result={simDeployResult} />}
        </Section>
      </div>

      {deployConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={() => setDeployConfirmOpen(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Confirm deployment</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to deploy the backoffice template to production for tenant <strong>{normalizedName}</strong>? This will create resources in resource group <strong>{resourceGroupName}</strong>.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeployConfirmOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600">Cancel</button>
              <button onClick={handleDeployConfirm} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Deploy</button>
            </div>
          </div>
        </div>
      )}

      {simDeployConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={() => setSimDeployConfirmOpen(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Confirm simulcast deployment</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to deploy the simulcast template to production for tenant <strong>{normalizedName}</strong>? This will create resources in resource group <strong>{simulcastResourceGroupName}</strong>.</p>
            <div className="flex gap-2">
              <button onClick={() => setSimDeployConfirmOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600">Cancel</button>
              <button onClick={handleSimDeployConfirm} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Deploy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantProvisioning;
